import React, {useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {setLogContext} from '@/logger'

import {useAuth} from '../../stores/AuthStore'
import {LoadingState} from '../../components'
import {stashReturnPath} from '../../navigation'

// What AuthRoute hands over when it bounces an unauthenticated visitor:
// <Navigate to={LOGIN_PATH} state={{from: location}} replace />.
interface LoginLocationState {
  from?: {
    pathname?: string
    search?: string
  }
}

export const LoginView: React.FC<React.PropsWithChildren> = () => {
  setLogContext('LoginView')

  const {loginWithIdp} = useAuth()
  const location = useLocation<LoginLocationState | undefined>()

  const from = location.state?.from
  // Derive the string outside the effect so the dependency is a stable
  // primitive; `location.state.from` is a fresh object on some navigations.
  const requestedPath = from
    ? `${from.pathname ?? ''}${from.search ?? ''}`
    : undefined

  useEffect(() => {
    // Capture where the user was actually headed before handing the page to
    // Auth0. That hop is a full document navigation, so React Router's in-memory
    // location state cannot survive it — the path has to be persisted, and
    // stashReturnPath validates it before it goes anywhere (AB#25795).
    //
    // Note this is only one of two entry points into re-authentication. The
    // other, TokenService's retries-exhausted redirect, never passes through
    // this view and stashes for itself.
    if (requestedPath) {
      stashReturnPath(requestedPath)
    }

    loginWithIdp()
  }, [loginWithIdp, requestedPath])

  return <LoadingState />
}
